import React, { useMemo, useState } from 'react';

import { styled } from '@mui/material/styles';
import classnames from 'classnames';
import { Button } from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';

const Container = styled('section')`
  .row {
    max-width: 800px;
  }

  form {
    display: flex;
    flex-direction: column;
    align-content: center;
    gap: 12px;

    .inline-inputs {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
    }
  }

  @media only screen and (max-width: 767px) {
    form {
      width: 100%;

      .inline-inputs {
        flex-direction: column;
      }
    }
  }
`;

type Props = {
  className?: string;
};

const recipients = ['zach@coastalcementllc.com', 'accounting@coastalcementllc.com'];

const ContactForm = ({ className }: Props) => {
  const [contactMethod, setContactMethod] = useState<string>('Email');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('South Carolina');
  const [addressZip, setAddressZip] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [questionsComments, setQuestionsComments] = useState('');

  const emailSubject = `Coastal Cement Web Inquiry`;

  const emailBody = useMemo(() => {
    let body = '';

    body += `Company: ${company}\n`;
    body += `Contact Name: ${name}\n`;
    body += `Preferred Contact Method: ${contactMethod} \n\n`;
    body += `Contact Information\n\n`;

    switch (contactMethod) {
      case 'Mail':
        body += `Address: ${addressStreet}, ${addressCity}, ${addressState} ${addressZip}\n`;
        break;
      default:
        body += `Phone: ${phone}\n`;
        body += `Email: ${email}\n`;
        break;
    }

    body += `Question/Comment: ${questionsComments}\n`;

    return body;
  }, [
    company,
    name,
    contactMethod,
    addressStreet,
    addressCity,
    addressState,
    addressZip,
    phone,
    email,
    questionsComments,
  ]);

  return (
    <Container className={classnames('ContactForm', className)}>
      <div className='row'>
        <h2>We're happy to hear from you</h2>
      </div>
      <div className='row'>
        <form method='post' action='#' className='contact-form'>
          <Input
            type='text'
            value={company}
            label='Company Name'
            onChange={(e) => setCompany(e.target.value)}
            required
          />
          <Input
            type='text'
            label='Your Name'
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            type='tel'
            label='Phone'
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            type='email'
            label='Email'
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <br />
          <Input
            type='text'
            label='Address'
            value={addressStreet}
            onChange={(e) => setAddressStreet(e.target.value)}
          />
          <div className='inline-inputs'>
            <Input
              type='text'
              label='City'
              value={addressCity}
              onChange={(e) => setAddressCity(e.target.value)}
            />
            <Select
              label='State'
              value={addressState}
              options={[
                { value: 'South Carolina', content: 'South Carolina' },
                { value: 'North Carolina', content: 'North Carolina' },
                { value: 'Other', content: 'Other' },
              ]}
              onChange={(value) => setAddressState(value)}
            />
            <Input
              type='text'
              label='ZIP'
              value={addressZip}
              onChange={(e) => setAddressZip(e.target.value)}
            />
          </div>
          <br />
          <div className='inline-inputs'>
            <Select
              label='Preferred Contact Method'
              value={contactMethod}
              options={[
                { value: 'Email', content: 'Email' },
                { value: 'Phone', content: 'Phone' },
                { value: 'Mail', content: 'Mail' },
              ]}
              onChange={(value) => setContactMethod(value)}
            />
          </div>
          <Input
            type='text'
            label='Questions/Comments'
            multiline
            required
            minRows={3}
            value={questionsComments}
            onChange={(e) => setQuestionsComments(e.target.value)}
          />
          <div className='actions'>
            <Button
              className='submit-button'
              href={`mailto:${recipients}?subject=${emailSubject}&body=${emailBody.replace(
                /\r\n|\r|\n/g,
                '%0D%0A'
              )}`}
              variant='full'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default ContactForm;
